import { DebugManager } from '@/managers/DebugManager'
import { IS_DEBUG_BUILD } from '@/utils/RuntimeEnv'
import { SoundManager } from '@/managers/sound/SoundManager'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { IntegrationAdapters } from '@modules/src/enums/IntegrationAdapters'
import { IModulesConfig } from '@modules/src/interfaces/IModulesConfig'
import { SPLASH_SCREEN_COMPONENT } from '@/gameConfigs/components/SplashScreenComponent'
import { BACKGROUND_COMPONENT } from '@/gameConfigs/components/BackgroundComponent'

import { LOGO_COMPONENT } from '@/gameConfigs/components/LogoComponent'
// import { INTRO_ANIMATION_COMPONENT } from '@/gameConfigs/components/IntroAnimationComponent'
import { JACKPOT_BOX_COMPONENT } from '@/gameConfigs/components/JackpotBoxComponent'
import { FREE_SPINS_SUMMARY_POPUPS_COMPONENT } from '@/gameConfigs/components/FreeSpinsSummaryPopupsComponent'
import { FREE_SPINS_COUNTER_COMPONENT } from '@/gameConfigs/components/FreeSpinsCounterComponent'
import { RESPIN_COUNTER_COMPONENT } from '@/gameConfigs/components/RespinCounterComponent'
import { POPUPS_TRANSITION_COMPONENT } from '@/gameConfigs/components/PopupsTransitionComponent'
import { FEATURE_INTRO_POPUP_COMPONENT } from '@/gameConfigs/components/FeatureIntroPopupComponent'
import { JACKPOT_LADDERS_COMPONENT } from '@/gameConfigs/components/JackpotLaddersComponent'
import { JACKPOT_POPUP_COMPONENT } from '@/gameConfigs/components/JackpotPopupComponent'
import { BUY_BONUS_COMPONENT } from '@/gameConfigs/components/BuyBonusComponent'
import { PANEL_COMPONENT } from '@/gameConfigs/components/PanelComponent'
import { TICKER_MESSAGE_COMPONENT } from '@/gameConfigs/components/TickerMessageComponent'
import { buildAssetUrl, getDeploymentBaseUrl } from '@/utils/AssetsUrlUtil'
import { REEL_COMPONENT } from './gameConfigs/components/ReelComponent'
import { JACKPOT_UPGRADE_COMPONENT } from '@/gameConfigs/components/JackpotUpgradeComponent'
import { MAX_EXPOSURE_POPUP_COMPONENT } from '@/gameConfigs/components/MaxExposurePopupComponent'
import { UNFINISHED_ROUND_POPUP_COMPONENT } from '@/gameConfigs/components/UnfinishedRoundPopupComponent'
import { GameStatesEnum } from '@/states/GameStatesEnum'

const ASSETS_BASED_PRELOADER_JSON = buildAssetUrl('/assets/json/PreloaderAssets.json')
const ASSETS_BASED_MAIN_JSON = buildAssetUrl('/assets/json/AssetsConfig.json')
const ASSETS_BASED_BG = buildAssetUrl('/assets/images/background.jpg')
const ASSETS_BASED_FREE_BG = buildAssetUrl('/assets/images/BackgroundFSLandscape.jpg')

/** Suppress Fast Play prompt across FS/H&W and until the next base-game spin. */
let suppressFastPlayPromptAfterFeature = false

const isFeatureBlockingFastPlayPrompt = (): boolean => {
    if (suppressFastPlayPromptAfterFeature) {
        return true
    }

    const model = Facade.model as any
    if (!model) {
        return false
    }

    return Boolean(model.isBonusGameNow || model.spinState?.isHoldAndWinActive || model.roundState?.isFreeSpins)
}

const isGameIdle = (): boolean => Facade.currentState === GameStatesEnum.IDLE_STATE

/**
 * A spin the player asked for from the panel. Every skip path — a tap anywhere on the screen,
 * the space bar, the Stop button — raises stopButtonClicked too, and counting those let the
 * prompt reach its threshold in the middle of a single round. Promo spins drive
 * spinButtonClicked on their own, so they are not the player being impatient either.
 */
const isPlayerStartedSpin = (): boolean => {
    if (!isGameIdle() || isFeatureBlockingFastPlayPrompt() || Facade.isPopupBlockingInput) {
        return false
    }

    return !(Facade.model as any)?.isPromoSpinsRound?.()
}

;(GameSignals as any).featureIntroRequested?.connect(() => {
    suppressFastPlayPromptAfterFeature = true
})
;(GameSignals as any).holdAndWinTransitionStart?.connect(() => {
    suppressFastPlayPromptAfterFeature = true
})
;(GameSignals as any).holdAndWinStartSpin?.connect(() => {
    suppressFastPlayPromptAfterFeature = true
})
;(GameSignals as any).holdAndWinStop?.connect(() => {
    suppressFastPlayPromptAfterFeature = true
})
GameSignals.startSpin?.connect(() => {
    const model = Facade.model as any
    const stillInFeature = Boolean(
        model?.isBonusGameNow || model?.spinState?.isHoldAndWinActive || model?.roundState?.isFreeSpins
    )
    if (!stillInFeature) {
        suppressFastPlayPromptAfterFeature = false
    }
})

const isLocalHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const TELEMETRY_TOGGLE_QUERY_PARAM = 'telemetry'
const TELEMETRY_TOGGLE_STORAGE_KEY = 'dg.telemetry.enabled'
const runtimeEnvRaw = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('env') || '' : ''
const runtimeHostRaw = typeof window !== 'undefined' ? window.location.hostname || '' : ''
const isStagingHost = runtimeHostRaw.toLowerCase().includes('staging')
const telemetryToggleParam =
    typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get(TELEMETRY_TOGGLE_QUERY_PARAM)?.toLowerCase() || ''
        : ''
const telemetryEnabledFromStorage =
    typeof window !== 'undefined' ? window.localStorage.getItem(TELEMETRY_TOGGLE_STORAGE_KEY) : null
const telemetryEnabledFromQuery = ['1', 'true', 'on', 'yes'].includes(telemetryToggleParam)
const telemetryDisabledFromQuery = ['0', 'false', 'off', 'no'].includes(telemetryToggleParam)

if (typeof window !== 'undefined') {
    if (telemetryEnabledFromQuery) {
        window.localStorage.setItem(TELEMETRY_TOGGLE_STORAGE_KEY, 'true')
    } else if (telemetryDisabledFromQuery) {
        window.localStorage.setItem(TELEMETRY_TOGGLE_STORAGE_KEY, 'false')
    }
}

const isTelemetryEnabledOnLocalhost =
    telemetryEnabledFromQuery || (!telemetryDisabledFromQuery && telemetryEnabledFromStorage === 'true')
const isTelemetryResolvedEnabled = !isLocalHost || isTelemetryEnabledOnLocalhost
//## Telemetry stays off in every environment for now, including staging and production, where the
//## resolution above would otherwise turn it on. The whole toggle — query param, stored preference,
//## endpoints — is left in place: clearing this constant is all it takes to hand control back to it.
const IS_TELEMETRY_TEMPORARILY_DISABLED = true
const isTelemetryEnabled = !IS_TELEMETRY_TEMPORARILY_DISABLED && isTelemetryResolvedEnabled
const SESSION_TELEMETRY_ENV =
    isLocalHost ||
    ['stage', 'staging', 'lcl', 'local', 'dev', 'sit', 'uat'].includes(runtimeEnvRaw.toLowerCase()) ||
    isStagingHost
        ? 'staging'
        : 'production'
const TELEMETRY_API_BASE_URL =
    SESSION_TELEMETRY_ENV === 'production'
        ? 'https://platform-api.dragongaming.com'
        : 'https://staging-platform-api.dragongaming.com'
const SESSION_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/session`
const ROUND_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/round`
const ECONOMY_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/economy`
// Served from public/ml/{locale}.js (legacy _ml.content dictionaries for this game). Anchored to the
// deployment and not to the assets base, since these ship next to index.html while the assets base
// may point at a CDN that only mirrors /assets. A root-relative path would miss them whenever the
// game is deployed under a subfolder, as the PHP launcher does.
// Bump when dictionary content changes: legacy script requests may otherwise reuse a stale browser/CDN copy.
const LOCAL_ML_TEMPLATE_URL = `${getDeploymentBaseUrl()}/ml/{locale}.js?v=popup-i18n-20260825c`

//## BigWin Aligments
export interface IBigWinLayout {
    scale: number
    position: { x: number; y: number }
    textFieldOffset: number
    fontSize: number
}

export const BIG_WIN_LAYOUT: Record<'landscape' | 'portrait', IBigWinLayout> = {
    landscape: {
        scale: 0.55,
        position: { x: 0, y: 150 },
        textFieldOffset: -85,
        fontSize: 20,
    },
    portrait: {
        scale: 0.75,
        position: { x: 0, y: 116 },
        textFieldOffset: -113,
        fontSize: 26,
    },
}

const ModulesConfig: IModulesConfig = {
    fastPlayPrompt: {
        threshold: 3,
        // No streak expiry: the prompt counts spins the player started, not how fast they tap.
        continuousClickWindowMs: 0,
        canTrackClick: (context) => context.source === 'spinButtonClicked' && isPlayerStartedSpin(),
        canShowPrompt: () => isGameIdle() && !isFeatureBlockingFastPlayPrompt() && !Facade.isPopupBlockingInput,
    },
    sessionTelemetry: {
        enabled: isTelemetryEnabled,
        endpoint: SESSION_TELEMETRY_ENDPOINT,
        roundEndpoint: ROUND_TELEMETRY_ENDPOINT,
        economyEndpoint: ECONOMY_TELEMETRY_ENDPOINT,
        apiKey: 'dg_alert_staging_oQeaBBCHEQ8_rSHU2e8q1pPOqXWZY3GMCq6JQl7thg0',
        apiKeyHeader: 'X-API-Key',
        schemaVersion: 1,
        gameId: 'soaring-eagle-hold-and-win',
        build: '1.0.0',
        env: SESSION_TELEMETRY_ENV,
    },
    translations: {
        enabled: true,
        defaultLocale: 'en',
        translationLocations: [LOCAL_ML_TEMPLATE_URL],
    },
    assetLoader: {
        enabled: true,
        default: {
            preloadAssets: [ASSETS_BASED_PRELOADER_JSON],
            mainAssets: [ASSETS_BASED_MAIN_JSON],
        },
    },
    debugPanel: {
        urlParamsConfig: true,
        hiddenUrlParams: ['rtp', 'jurisdiction'],
        forceGsapTimelineTimeout: true,
        timelineConfig: true,
        forceDrawCallsPanel: false,
        countDrawCalls: true,
        folders: DebugManager.createFolders(),
        // Drives the FPS meter too. Keyed on the build, not the hostname, so a production build tested
        // on localhost hides it exactly as it would in production.
        enabled: IS_DEBUG_BUILD,
    },
    gameBackground: {
        enabled: true,
        backgrounds: [
            {
                url: ASSETS_BASED_BG,
                triggerSignals: [GameSignals.displaySplashScreenState, GameSignals.showBaseGameBackground],
            },
            {
                url: ASSETS_BASED_FREE_BG,
                triggerSignals: [GameSignals.showFreeSpinsBackground],
            },
        ],
    },
    components: {
        enabled: true,
        items: [
            BACKGROUND_COMPONENT,
            LOGO_COMPONENT,
            JACKPOT_BOX_COMPONENT,
            REEL_COMPONENT,
            // INTRO_ANIMATION_COMPONENT,
            TICKER_MESSAGE_COMPONENT,
            POPUPS_TRANSITION_COMPONENT,
            FEATURE_INTRO_POPUP_COMPONENT,
            JACKPOT_LADDERS_COMPONENT,
            FREE_SPINS_COUNTER_COMPONENT,
            BUY_BONUS_COMPONENT,
            SPLASH_SCREEN_COMPONENT,
            PANEL_COMPONENT,
            RESPIN_COUNTER_COMPONENT,
            FREE_SPINS_SUMMARY_POPUPS_COMPONENT,
            JACKPOT_POPUP_COMPONENT,
            JACKPOT_UPGRADE_COMPONENT,
            MAX_EXPOSURE_POPUP_COMPONENT,
            UNFINISHED_ROUND_POPUP_COMPONENT,
        ],
    },
    sounds: {
        enabled: true,
        soundsConfigPath: SoundManager.createConfigPath(),
        sounds: SoundManager.createSounds(),
        useAudioSprite: true,
    },
    bigWin: {
        enabled: true,
        scale: BIG_WIN_LAYOUT.landscape.scale,
        position: BIG_WIN_LAYOUT.landscape.position,
        customFont: {
            textFieldOffset: BIG_WIN_LAYOUT.landscape.textFieldOffset,
            fontName: 'commendationBitmapfonts',
            fontSize: BIG_WIN_LAYOUT.landscape.fontSize,
            letterSpacing: 0,
        },
    },
    integrations: {
        enabled: true,
        adapter: IntegrationAdapters.SLOTS_ADAPTER,
    },
}

export default ModulesConfig
